import { Fragment, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { Project } from '@/api/client';
import { api } from '@/api/client';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { Badge } from '@/components/ui/Badge/Badge';
import { DeadlineRemainingBadge } from '@/components/ui/DeadlineRemainingBadge/DeadlineRemainingBadge';
import { ContentPasteSearchIcon } from '@/components/ui/Icons';
import { getBaseStatus } from '@/utils/projectStatuses';
import { groupProjectsForPickerSections } from '@/utils/pickerProgressGroups';
import '@/components/ui/SectionDividerWithLines/SectionDividerWithLines.css';
import '@/components/ui/overlayPickerBadges.css';
import './ProjectCombo.css';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 2;

interface ProjectComboProps {
  label?: string;
  value: string;
  onChange: (projectId: string, project?: Project) => void;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  /** Фильтр: показывать только проекты, в которых есть этот чтец (person_id) */
  readerIdFilter?: string;
  /** Исключить проекты со статусом (например "Готов" для модалки сессий) */
  excludeState?: string;
  /** Проект для немедленного отображения (например, при редактировании сессии — id и name уже известны) */
  initialProject?: { id: string; name: string; state?: string | null };
  /** Смена ключа (напр. при открытии модалки) — сбрасывает отложенный поиск в актуальную строку */
  debounceFlushKey?: string | number;
}

export function ProjectCombo({
  label,
  value,
  onChange,
  placeholder = 'Введите название для поиска',
  required,
  fullWidth,
  disabled,
  readerIdFilter,
  excludeState,
  initialProject,
  debounceFlushKey,
}: ProjectComboProps) {
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement | null>(null);
  const scrollToHighlightRef = useRef(false);

  const searchQuery = inputValue.trim();
  /** Портал монтируется только при portalEligible; хук позиции должен подписаться на то же, иначе layout-effect отработает при open без DOM панели и не повторится после первого символа */
  const portalEligible = open && (!!readerIdFilter || searchQuery.length > 0);
  const dropdownStyle = useDropdownPosition(containerRef, portalEligible, { dropdownRef: dropdownWrapRef });
  const debouncedSearch = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS, debounceFlushKey);
  const [page, setPage] = useState(0);
  const [accumulatedItems, setAccumulatedItems] = useState<Project[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const shouldFetchSearch = debouncedSearch.length >= MIN_SEARCH_LENGTH || (!!readerIdFilter && open);
  const { data: searchResponse, isFetching, isPlaceholderData } = useQuery({
    queryKey: ['projects', 'search', debouncedSearch, readerIdFilter ?? '', page],
    queryFn: async () => {
      const res = await api.projects.list({
        q: debouncedSearch || undefined,
        readerId: readerIdFilter || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (Array.isArray(res)) return { items: res, hasMore: false };
      return res;
    },
    enabled: shouldFetchSearch,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!shouldFetchSearch) {
      setPage(0);
      setAccumulatedItems([]);
      setHasMore(false);
      return;
    }
    if (!searchResponse || isPlaceholderData) return;
    if (page === 0) {
      setAccumulatedItems(searchResponse.items);
      setHasMore(searchResponse.hasMore);
    } else {
      setAccumulatedItems((prev) => [...prev, ...searchResponse.items]);
      setHasMore(searchResponse.hasMore);
    }
  }, [searchResponse, page, shouldFetchSearch, debouncedSearch, isPlaceholderData]);

  useEffect(() => {
    if (!shouldFetchSearch) return;
    setPage(0);
  }, [debouncedSearch, readerIdFilter, shouldFetchSearch]);

  const handleLoadMore = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPage((p) => p + 1);
  }, []);

  const searchResults = accumulatedItems;

  const { data: selectedProjectData } = useQuery({
    queryKey: ['project', value],
    queryFn: () => api.projects.get(value),
    enabled: !!value,
    staleTime: 60_000,
  });

  const immediateProjectRef = useRef<Project | null>(null);

  const selectedProject = (() => {
    if (!value) return undefined;
    if (selectedProjectData) return selectedProjectData;
    if (immediateProjectRef.current?.id === value) return immediateProjectRef.current;
    if (initialProject?.id === value) return initialProject as Project;
    return undefined;
  })();

  useEffect(() => {
    if (!value) immediateProjectRef.current = null;
  }, [value]);

  const baseFiltered = useMemo(() => {
    const base =
      excludeState != null && excludeState !== ''
        ? searchResults.filter((p) => getBaseStatus(p.state) !== excludeState)
        : searchResults;
    return base;
  }, [searchResults, excludeState]);

  const pickerSections = useMemo(
    () => groupProjectsForPickerSections(baseFiltered, readerIdFilter),
    [baseFiltered, readerIdFilter]
  );

  const flatPickerOptions = useMemo(() => pickerSections.flatMap((s) => s.projects), [pickerSections]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [open, flatPickerOptions.length]);

  useEffect(() => {
    if (!open) return;
    if (!scrollToHighlightRef.current) return;
    scrollToHighlightRef.current = false;
    const el = listRef.current?.querySelector(`[data-option-index="${highlightIndex}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  useClickOutside(containerRef, () => setOpen(false), {
    excludeSelector: '[data-combo-dropdown]',
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if ((e.key === 'Enter' || e.key === 'ArrowDown') && (searchQuery.length > 0 || selectedProject)) {
        e.preventDefault();
        if (selectedProject) setInputValue(selectedProject.name);
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      scrollToHighlightRef.current = true;
      setHighlightIndex((i) => Math.min(i + 1, Math.max(0, flatPickerOptions.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      scrollToHighlightRef.current = true;
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = flatPickerOptions[highlightIndex];
      if (p) {
        immediateProjectRef.current = p;
        onChange(p.id, p);
        setInputValue('');
        setOpen(false);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleClear = () => {
    immediateProjectRef.current = null;
    onChange('');
    setInputValue('');
    setOpen(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    if (selectedProject) setInputValue(selectedProject.name);
    setOpen(true);
    if (!selectedProject) {
      queueMicrotask(() => inputRef.current?.focus({ preventScroll: true }));
    }
  };

  return (
    <div className={`form__field project-combo ${fullWidth ? 'form__field_full project-combo_form-field-flex' : ''}`}>
      {label && (
        <label className="form__label">
          {label}
          {required && ' *'}
        </label>
      )}
      <div
        ref={containerRef}
        role={selectedProject ? 'combobox' : undefined}
        aria-expanded={selectedProject ? open : undefined}
        aria-haspopup={selectedProject ? 'listbox' : undefined}
        aria-controls={selectedProject ? 'project-combo-list' : undefined}
        tabIndex={selectedProject ? 0 : undefined}
        className={`project-combo__field ${open ? 'project-combo__field_open' : ''} ${disabled ? 'project-combo__field_disabled' : ''} ${value ? 'project-combo__field_has-clear' : ''} ${isFetching ? 'project-combo__field_loading' : ''} ${!selectedProject ? 'project-combo__field_search-mode' : ''}`}
        onMouseDown={(e) => {
          if (disabled || selectedProject) return;
          const el = e.target as HTMLElement;
          if (el === inputRef.current) return;
          if (inputRef.current?.contains(el)) return;
          if (el.closest('button')) return;
          inputRef.current?.focus({ preventScroll: true });
        }}
        onClick={handleOpen}
        onKeyDown={selectedProject ? handleKeyDown : undefined}
      >
        {selectedProject ? (
          <span className="project-combo__value overlay-picker-badge-row">
            <Badge variant="status" status={selectedProject.state} />
            <DeadlineRemainingBadge deadline={selectedProject.deadline} projectState={selectedProject.state} />
            <span className="project-combo__name">{selectedProject.name}</span>
          </span>
        ) : (
          <input
            ref={inputRef}
            className="project-combo__input"
            value={inputValue}
            onChange={(e) => {
              const v = e.target.value;
              setInputValue(v);
              setOpen(v.trim().length > 0);
            }}
            onFocus={() => {
              if (disabled) return;
              setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? '' : placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls="project-combo-list"
          />
        )}
        {value && !disabled && (
          <ComboClearButton
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          />
        )}
      </div>
      {portalEligible &&
        createPortal(
          <div
            ref={dropdownWrapRef}
            className="project-combo__dropdown-wrap"
            data-combo-dropdown
            style={dropdownStyle}
          >
            {!readerIdFilter && searchQuery.length < MIN_SEARCH_LENGTH ? (
              <div className="project-combo__hint">Введите минимум 2 символа для поиска</div>
            ) : flatPickerOptions.length === 0 && isFetching ? (
              <div className="project-combo__hint">Загрузка…</div>
            ) : flatPickerOptions.length === 0 ? (
              <div className="project-combo__hint">Нет результатов</div>
            ) : (
              <>
            <OverlayScrollArea
              contentRef={listRef}
              element="ul"
              id="project-combo-list"
              className="project-combo__dropdown"
              role="listbox"
            >
              {(() => {
                let optionIdx = 0;
                return pickerSections.map((sec) => (
                  <Fragment key={sec.label}>
                    <li role="presentation" className="project-combo__section-divider">
                      <div className="section-divider-with-lines section-divider-with-lines_full-bleed">
                        <span className="section-divider-with-lines__label">{sec.label}</span>
                      </div>
                    </li>
                    {sec.projects.map((p) => {
                      const i = optionIdx++;
                      return (
                        <li
                          key={p.id}
                          role="option"
                          data-option-index={i}
                          aria-selected={p.id === value}
                          className={`project-combo__option ${i === highlightIndex ? 'project-combo__option_highlight' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            immediateProjectRef.current = p;
                            onChange(p.id, p);
                            setInputValue('');
                            setOpen(false);
                          }}
                          onMouseEnter={() => setHighlightIndex(i)}
                        >
                          <span className="project-combo__option-inner overlay-picker-badge-row">
                            <Badge variant="status" status={p.state} />
                            <DeadlineRemainingBadge deadline={p.deadline} projectState={p.state} />
                            <span className="project-combo__name project-combo__name_wrap">{p.name}</span>
                          </span>
                        </li>
                      );
                    })}
                  </Fragment>
                ));
              })()}
            </OverlayScrollArea>
            {hasMore && (
              <button
                type="button"
                className="project-combo__load-more"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleLoadMore(e);
                }}
              >
                <ContentPasteSearchIcon size={18} />
                Загрузить ещё
              </button>
            )}
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  handler: () => void,
  options?: { excludeSelector?: string }
) {
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (options?.excludeSelector && (target as Element).closest?.(options.excludeSelector)) return;
      handler();
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [ref, handler, options?.excludeSelector]);
}
